import React from "react";
import { Box, Stack } from "@mui/material";
import { Custom } from "components";
import { useApp, useTheme } from "contexts";
import { Socials } from "assets/data";
import { SocialType } from "types";
import { EnumTheme } from "utils/enums";

export const Contact = () => {
  const { theme } = useTheme();
  const { t } = useApp();
  const isDark = theme.id === EnumTheme.Dark;

  const emailSocial = Socials.find((s: SocialType) => s.name.toLowerCase() === "email");
  const linkedInSocial = Socials.find((s: SocialType) => s.name.toLowerCase() === "linkedin");
  const emailHref = emailSocial?.href;
  const linkedInHref = linkedInSocial?.href;

  return (
    <Box
      component="section"
      id="contact"
      sx={{
        pt: { xs: theme.spacing.xl, md: theme.spacing.xxl },
        pb: { xs: theme.spacing.xxl, md: theme.spacing.xxxl },
        width: "100%",
        position: "relative",
      }}
    >
      <Box
        sx={{
          backgroundColor: theme.color.theme.color,
          border: `2px solid ${isDark ? theme.color.border.accent : "#191b22"}`,
          p: { xs: theme.spacing.lg, md: theme.spacing.xxl },
          boxShadow: `6px 6px 0px 0px ${isDark ? "#000000" : "#191b22"}`,
          borderRadius: theme.border.square,
          position: "relative",
          transition: "transform 0.15s ease, box-shadow 0.15s ease",
          "&:hover": {
            transform: "translate(-2px, -2px)",
            boxShadow: `8px 8px 0px 0px ${isDark ? "#000000" : "#191b22"}`,
          },
        }}
      >
        <Box sx={{ maxWidth: 720 }}>
          <Box
            sx={{
              display: "inline-block",
              px: theme.spacing.xs,
              py: theme.spacing.xxs,
              backgroundColor: theme.color.primary.color,
              color: theme.color.primary.text,
              fontFamily: '"Space Mono", monospace',
              fontSize: theme.font.xxs,
              fontWeight: theme.font.bold,
              letterSpacing: "0.1em",
              mb: theme.spacing.sm,
            }}
          >
            {t.contact?.badge}
          </Box>

          <Custom.Typography
            variant="h2"
            sx={{
              fontSize: { xs: theme.font.xl, md: theme.font.xxl },
              fontWeight: theme.font.extraBold,
              letterSpacing: "-0.02em",
              mb: theme.spacing.md,
            }}
          >
            {t.contact?.title}
          </Custom.Typography>

          <Custom.Typography
            variant="body1"
            sx={{
              color: theme.color.font.accent,
              lineHeight: 1.65,
              mb: theme.spacing.lg,
              fontSize: { xs: theme.font.sm, md: theme.font.md },
            }}
          >
            {t.contact?.description}
            </Custom.Typography>

          <Stack direction="row" spacing={theme.spacing.md} flexWrap="wrap" gap={theme.spacing.xs}>
            <Custom.Button
              href={emailHref}
              sx={{ px: theme.spacing.lg, py: theme.spacing.sm }}
            >
              {t.contact?.cta_email}
            </Custom.Button>
            <Custom.Button
              secondary
              href={linkedInHref}
              target="_blank"
              rel="noreferrer"
              sx={{ px: theme.spacing.lg, py: theme.spacing.sm }}
            >
              {t.contact?.cta_linkedin}
            </Custom.Button>
          </Stack>
        </Box>

        {/* Corner Micro Matrix Accent */}
        <Box
          sx={{
            position: "absolute",
            top: 16,
            right: 16,
            display: { xs: "none", sm: "grid" },
            gridTemplateColumns: "repeat(3, 6px)",
            gap: theme.spacing.xs,
          }}
        >
          <Box sx={{ width: 6, height: 6, backgroundColor: theme.color.primary.color }} />
          <Box sx={{ width: 6, height: 6, backgroundColor: theme.color.primary.color }} />
          <Box sx={{ width: 6, height: 6, backgroundColor: theme.color.primary.color }} />
          <Box sx={{ width: 6, height: 6, backgroundColor: theme.color.primary.color }} />
          <Box sx={{ width: 6, height: 6, backgroundColor: theme.color.secondary.text }} />
          <Box sx={{ width: 6, height: 6, backgroundColor: theme.color.primary.color }} />
          <Box sx={{ width: 6, height: 6, backgroundColor: theme.color.primary.color }} />
          <Box sx={{ width: 6, height: 6, backgroundColor: theme.color.primary.color }} />
          <Box sx={{ width: 6, height: 6, backgroundColor: theme.color.primary.color }} />
        </Box>
      </Box>
    </Box>
  );
};